import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { TRAINING_STAGE_LABEL, type Project, type TrainingState } from '@allemnii-pulse/shared';
import { useOrgProjects } from '../../lib/useOrgProjects.js';
import { useDocument } from '../../lib/firestore.js';
import { Card } from '../../components/ui/Card.js';
import { useProjectDocPointers, totalTaskCount } from './useProjectDocPointers.js';

type StageStatus = 'none' | 'draft' | 'accepted';

/** Compact version of ProjectOverviewPage's stage-card icon — same three states, sized to sit
 * in a dashboard row instead of a full card. */
function StageDot({ status, label }: { status: StageStatus; label: string }) {
  return (
    <span
      title={`${label}: ${status}`}
      className={`flex h-4 w-4 items-center justify-center rounded-full text-[9px] font-bold ${
        status === 'accepted'
          ? 'bg-success text-white'
          : status === 'draft'
            ? 'bg-accent-soft text-accent-soft-fg'
            : 'border border-border text-muted'
      }`}
    >
      {status === 'accepted' ? '✓' : status === 'draft' ? '…' : ''}
    </span>
  );
}

/** A training project has no PRD/TDD — it shows the name of the step it's on instead. */
function TrainingStage({ projectId }: { projectId: string }) {
  const { data: state } = useDocument<TrainingState>(`projects/${projectId}/training/state`);
  return (
    <span className="rounded-full bg-accent-soft px-2 py-0.5 text-[10px] font-medium text-accent-soft-fg">
      {state ? TRAINING_STAGE_LABEL[state.stage] : '…'}
    </span>
  );
}

function PrdTddStages({ project }: { project: Project }) {
  const { prd, tdd, loading } = useProjectDocPointers(project.id);
  const total = totalTaskCount(project);
  return (
    <div className="flex items-center gap-1.5">
      <StageDot label="PRD" status={loading ? 'none' : (prd?.status ?? 'none')} />
      <StageDot label="TDD" status={loading ? 'none' : (tdd?.status ?? 'none')} />
      <StageDot label="Tasks" status={total > 0 ? 'accepted' : 'none'} />
    </div>
  );
}

function ProjectStageRow({ project }: { project: Project }) {
  const total = totalTaskCount(project);
  const done = project.counters?.byStatus?.done ?? 0;
  const pct = total ? Math.round((done / total) * 100) : 0;

  return (
    <Link
      to={`/projects/${project.id}`}
      className="flex items-center gap-3 p-3 transition-colors hover:bg-surface-2"
    >
      <span className="flex-1 truncate text-sm">{project.name}</span>
      {project.projectType === 'training' ? (
        <TrainingStage projectId={project.id} />
      ) : (
        <PrdTddStages project={project} />
      )}
      <span className="w-9 text-right font-mono text-xs text-muted">{pct}%</span>
    </Link>
  );
}

/** Org-wide PRD → TDD → Tasks pipeline: one row per project the user can see, each showing
 * where it stands in the three stages plus task completion. Same query pattern and
 * `memberUids array-contains` requirement as ProjectsListPage/BlockersWidget. */
export function StagePipelineWidget() {

  const { data: projects, loading } = useOrgProjects();
  const activeProjects = useMemo(() => projects.filter((p) => p.status === 'active'), [projects]);

  return (
    <Card className="flex flex-col overflow-hidden">
      <div className="flex items-center gap-2 border-b border-border p-3">
        <span className="text-xs font-medium">Stage pipeline</span>
        <span className="ml-auto text-[10px] text-muted">PRD · TDD · Tasks</span>
      </div>

      {loading ? (
        <p className="p-4 text-sm text-muted">Loading…</p>
      ) : activeProjects.length === 0 ? (
        <p className="p-8 text-center text-sm text-muted">No active projects.</p>
      ) : (
        <div className="flex flex-col divide-y divide-border">
          {activeProjects.map((p) => (
            <ProjectStageRow key={p.id} project={p} />
          ))}
        </div>
      )}
    </Card>
  );
}
